import React, { useState } from "react";
import { CheckCircle2, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/auth";
import CehrBrandmark from "../assets/cehr-brandmark.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) return setError("This reset link is incomplete. Request a new link.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirmPassword) return setError("Passwords do not match.");
    try {
      setLoading(true);
      const response = await resetPassword(token, password);
      setSuccess(response?.message || "Your password has been reset.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-gradient-to-br from-[#f8f5ff] via-white to-[#eef2ff] px-4 py-8 font-Outfit">
      <section className="w-full max-w-md rounded-3xl border border-blue-100 bg-white p-6 shadow-xl shadow-blue-100/60 sm:p-8">
        <div className="mb-6 flex items-center gap-3"><div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm"><img src={CehrBrandmark} alt="Cephas HR" className="h-full w-full object-contain" /></div><div><p className="font-bold text-gray-900">Cephas HR</p><p className="text-xs text-gray-500">Create a new password</p></div></div>
        <h1 className="text-2xl font-bold text-gray-900">Reset password</h1>
        <p className="mt-2 text-sm leading-6 text-gray-500">Choose a strong password you haven’t used for this account before.</p>
        {success ? (
          <div className="mt-6"><div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"><CheckCircle2 className="mb-2 h-5 w-5" />{success}</div><Link to="/login" className="mt-5 block w-full rounded-xl bg-[#6d35c5] py-3 text-center font-semibold text-white">Continue to sign in</Link></div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {[["New password", password, setPassword], ["Confirm password", confirmPassword, setConfirmPassword]].map(([label, value, setter]) => (
              <label key={label} className="block text-sm font-medium text-gray-700">{label}<span className="relative mt-2 block"><LockKeyhole className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" /><input required minLength={8} maxLength={72} type={showPassword ? "text" : "password"} value={value} onChange={(event) => setter(event.target.value)} className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-11 outline-none transition focus:border-[#1233CC] focus:ring-4 focus:ring-blue-100" />{label === "New password" ? <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button> : null}</span></label>
            ))}
            <p className="text-xs text-gray-400">Use at least 8 characters.</p>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <button disabled={loading || !token} className="w-full rounded-xl bg-gradient-to-r from-[#1233CC] to-[#00BAFF] py-3 font-semibold text-white shadow-lg shadow-blue-200 disabled:opacity-50">{loading ? "Updating password..." : "Reset password"}</button>
            {!token ? <Link to="/forgot-password" className="block text-center text-sm font-semibold text-[#1233CC]">Request a new reset link</Link> : null}
          </form>
        )}
      </section>
    </main>
  );
}
